import { TIER_DISPLAY, TIERS } from "../../constants/membership";

const TRUST_TIER_META = {
  ELITE: { label: "Elite", color: "#10B981" },
  HIGH: { label: "High", color: "#22C55E" },
  GOOD: { label: "Good", color: "#FDB022" },
  FAIR: { label: "Fair", color: "#F97316" },
  LOW: { label: "Low", color: "#EF4444" },
};

function toFiniteNumber(value) {
  if (value == null || value === "") return null;
  const parsed = typeof value === "number" ? value : Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function formatNumber(value, digits = 1) {
  const normalized = toFiniteNumber(value);
  return normalized == null ? null : normalized.toFixed(digits);
}

export function buildMatchPlayerCardData(player) {
  const membershipTier = String(player?.membership?.tier || TIERS.FREE)
    .trim()
    .toLowerCase();
  const membershipDisplay =
    TIER_DISPLAY[membershipTier] || TIER_DISPLAY[TIERS.FREE];
  const trustTierKey = String(player?.reputation?.trustTier || "")
    .trim()
    .toUpperCase();
  const trustTierDisplay = TRUST_TIER_META[trustTierKey] || null;
  const totalMatches = toFiniteNumber(player?.stats?.totalMatches);
  const totalKills = toFiniteNumber(player?.stats?.kills);
  const explicitAvgKills = toFiniteNumber(player?.stats?.avgKills);
  const avgKills =
    explicitAvgKills ??
    (totalKills != null &&
    totalMatches != null &&
    totalMatches > 0
      ? totalKills / totalMatches
      : null);

  const repScore = toFiniteNumber(
    player?.reputation?.overallScore ?? player?.reputation?.overallTrustScore
  );
  const eloValue = toFiniteNumber(player?.elo);

  const founderBadge = player?.founderBadge?.granted
    ? { granted: true, number: player.founderBadge.number || null }
    : null;

  return {
    identity: {
      displayName: player?.nickname || player?.name || "Unknown player",
    },
    membership: {
      tier: membershipTier,
      badgeLabel:
        membershipTier === TIERS.FREE ? null : membershipDisplay.badge,
      color: membershipDisplay.color,
    },
    verification: {
      isVerified: player?.kyc?.badgeEligible === true,
    },
    founderBadge,
    rating3: {
      value: toFiniteNumber(
        player?.rating3?.current ?? player?.rating3
      ),
      subRatings:
        player?.rating3Sub || player?.rating3?.subRatings || null,
    },
    reputation: repScore != null || trustTierDisplay
      ? {
          score: repScore,
          scoreText:
            repScore == null ? null : `${Math.round(repScore)}/100`,
          trustTier: trustTierDisplay ? trustTierKey : null,
          label: trustTierDisplay ? trustTierDisplay.label : null,
          color: trustTierDisplay ? trustTierDisplay.color : null,
        }
      : null,
    stats: {
      totalMatches: {
        value: totalMatches,
        display: totalMatches == null ? null : String(Math.round(totalMatches)),
      },
      winRate: {
        value: toFiniteNumber(player?.stats?.winRate),
        display: formatNumber(player?.stats?.winRate, 0),
      },
      avgKills: {
        value: avgKills,
        display: formatNumber(avgKills, 0),
      },
      kills: {
        value: totalKills,
        display: totalKills == null ? null : String(Math.round(totalKills)),
      },
      kd: {
        value: toFiniteNumber(player?.stats?.kd),
        display: formatNumber(player?.stats?.kd, 2),
      },
      kr: {
        value: toFiniteNumber(player?.stats?.kr),
        display: formatNumber(player?.stats?.kr, 2),
      },
      adr: {
        value: toFiniteNumber(player?.stats?.adr),
        display: formatNumber(player?.stats?.adr, 1),
      },
      hsPercent: {
        value: toFiniteNumber(player?.stats?.hsPercent),
        display: formatNumber(player?.stats?.hsPercent, 0),
      },
    },
    elo: {
      value: eloValue,
      display: eloValue == null ? null : String(Math.round(eloValue)),
    },
  };
}

export function getMatchPlayerCardMetricGroups(card, viewMode) {
  const matchesMetric = { label: "Matches", value: card.stats.totalMatches.display };
  const winRateMetric = {
    label: "Win rate",
    value:
      card.stats.winRate.display != null
        ? `${card.stats.winRate.display}%`
        : null,
  };
  const avgKillsMetric = {
    label: "Avg kills",
    value: card.stats.avgKills.display ?? card.stats.kills.display,
  };
  const kdMetric = { label: "K/D", value: card.stats.kd.display };
  const krMetric = { label: "K/R", value: card.stats.kr.display };
  const adrMetric = { label: "ADR", value: card.stats.adr.display };
  const hsMetric = {
    label: "HS%",
    value:
      card.stats.hsPercent.display != null
        ? `${card.stats.hsPercent.display}%`
        : null,
  };
  if (viewMode === "faceit") {
    return {
      primary: [
        matchesMetric,
        winRateMetric,
        avgKillsMetric,
        kdMetric,
      ],
      secondary: [krMetric, adrMetric, hsMetric],
      inline: [],
    };
  }

  if (viewMode === "ouro") {
    return {
      primary: [winRateMetric, kdMetric, adrMetric, matchesMetric],
      secondary: [avgKillsMetric, hsMetric],
      inline: [],
    };
  }

  return {
    primary: [winRateMetric, kdMetric, adrMetric, matchesMetric],
    secondary: [],
    inline: [hsMetric],
  };
}
